import { ReactElement, useMemo } from 'react'
import { SceneLineMarker } from '@scene'
import { Vector3 } from 'three'
import { ImmutableVector3 } from '@modugen/ui-toolkit'

interface Props {
  center: { x: number; y: number; z: number }
  direction: { x: number; y: number; z: number }
  length: number
  width: number
  overhangLeft?: number
  overhangRight?: number
  overhangFront?: number
  overhangBack?: number
}

const SupportGeometrySceneMarker = ({
  center,
  direction,
  length,
  width,
  overhangLeft = 0,
  overhangRight = 0,
  overhangFront = 0,
  overhangBack = 0,
}: Props): ReactElement => {
  const lines = useMemo(() => {
    const centerVector = new Vector3(center.x, center.y, center.z)
    const directionVector = new Vector3(direction.x, direction.y, direction.z).normalize()
    const upVector = new Vector3(0, 0, 1)
    const widthVector = upVector.clone().cross(directionVector).normalize()

    const halfLength = length / 2
    const halfWidth = width / 2
    const lengthBefore = halfLength + overhangLeft
    const lengthAfter = halfLength + overhangRight
    const widthNegative = halfWidth + overhangBack
    const widthPositive = halfWidth + overhangFront

    const p1 = centerVector
      .clone()
      .addScaledVector(directionVector, -lengthBefore)
      .addScaledVector(widthVector, -widthNegative)
    const p2 = centerVector
      .clone()
      .addScaledVector(directionVector, lengthAfter)
      .addScaledVector(widthVector, -widthNegative)
    const p3 = centerVector
      .clone()
      .addScaledVector(directionVector, lengthAfter)
      .addScaledVector(widthVector, widthPositive)
    const p4 = centerVector
      .clone()
      .addScaledVector(directionVector, -lengthBefore)
      .addScaledVector(widthVector, widthPositive)

    return [
      [new ImmutableVector3(p1.x, p1.y, p1.z), new ImmutableVector3(p2.x, p2.y, p2.z)],
      [new ImmutableVector3(p2.x, p2.y, p2.z), new ImmutableVector3(p3.x, p3.y, p3.z)],
      [new ImmutableVector3(p3.x, p3.y, p3.z), new ImmutableVector3(p4.x, p4.y, p4.z)],
      [new ImmutableVector3(p4.x, p4.y, p4.z), new ImmutableVector3(p1.x, p1.y, p1.z)],
    ]
  }, [center, direction, length, width, overhangBack, overhangFront, overhangLeft, overhangRight])

  return (
    <SceneLineMarker
      lines={lines}
      color="blue"
      lineWidth={4}
      // renderOrder={1000}
      disableDepthTest={true}
    />
  )
}

export default SupportGeometrySceneMarker
